/**
 * env.ts — helpers para leer variables de entorno de forma segura.
 *
 * Soporta dos universos:
 *  - Browser (Vite): variables expuestas vía `import.meta.env` (deben empezar con `VITE_`).
 *  - Node / Edge (scripts y tests): `process.env`.
 *
 * Uso:
 *   const url = requireEnv('VITE_SUPABASE_URL');           // tira si falta
 *   const debug = getEnv('VITE_DEBUG', 'false');           // opcional con fallback
 */

/**
 * Lee una variable de entorno desde `import.meta.env` o `process.env`,
 * sin tirar. Usado internamente por `requireEnv` y `getEnv`.
 */
function readRawEnv(name: string): string | undefined {
  // import.meta.env solo existe en bundles de Vite. Lo accedemos sin optional
  // chaining porque Vite reemplaza la sintaxis EXACTA `import.meta.env` con el
  // objeto inline en build-time; usar `import.meta?.env` rompe ese matching
  // textual y deja la propiedad como undefined en runtime.
  // Para entornos sin import.meta (Node CJS) el try/catch lo cubre.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const viteEnv = (import.meta as any).env as
      | Record<string, string | undefined>
      | undefined;
    if (viteEnv && typeof viteEnv[name] === 'string' && viteEnv[name] !== '') {
      return viteEnv[name];
    }
  } catch {
    // import.meta no soportado en este runtime; seguir.
  }

  if (typeof process !== 'undefined' && process.env && typeof process.env[name] === 'string') {
    const value = process.env[name];
    if (value !== '') return value;
  }

  return undefined;
}

/**
 * Devuelve el valor de una variable obligatoria; tira un error claro si falta.
 * El mensaje incluye sugerencia de dónde definirla según el prefijo.
 */
export function requireEnv(name: string): string {
  const value = readRawEnv(name);
  if (value === undefined) {
    const hint = name.startsWith('VITE_')
      ? 'Definir en .env.local (cliente) y reiniciar Vite.'
      : 'Definir en el entorno del proceso (.env, shell, secrets de CI/Edge).';
    throw new Error(`[env] Falta la variable de entorno requerida "${name}". ${hint}`);
  }
  return value;
}

/**
 * Devuelve el valor opcional de una variable, o el `fallback` si no existe.
 */
export function getEnv(name: string, fallback: string): string {
  const value = readRawEnv(name);
  return value ?? fallback;
}
